import React from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Typography,
  Badge,
  ProgressStepper,
  Step,
  StepButton,
  StepLabel,
  Skeleton,
  SkeletonContent,
  SkeletonBlock,
  SkeletonLine,
} from '@one-thd/sui-atomic-components';
import { ArrowForward } from '@one-thd/sui-icons';

export const SweepstakesEntryCard = ({
  sweepstakesData,
  onDetailsClick,
  onStepClick,
  isLoading,
  hasError,
}) => {
  const handleDetailsClick = (event) => {
    event.stopPropagation();
    if (onDetailsClick && !isLoading && !hasError) {
      onDetailsClick(sweepstakesData);
    }
  };

  const handleStepClick = (step) => {
    if (onStepClick && !isLoading && !hasError && sweepstakesData?.isOptedIn) {
      onStepClick(step, sweepstakesData);
    }
  };

  const formatExpirationDate = (dateString) => {
    if (!dateString) return '';

    const expirationDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expirationDate.setHours(0, 0, 0, 0);

    if (expirationDate.getTime() === today.getTime()) {
      return 'Expires Today';
    }

    return `Expires ${expirationDate.toLocaleDateString()}`;
  };

  if (hasError) {
    return (
      <Card grow data-testid="sweepstakes-entry-card-error">
        <div className="sui-text-center sui-w-full">
          <Typography variant="body-base" color="danger" data-testid="sweepstakes-error-message">
            Unable to load sweepstakes
          </Typography>
        </div>
      </Card>
    );
  }

  return (
    <Card grow data-testid="sweepstakes-entry-card">
      {isLoading ? (
        <Skeleton grow disablePadding data-testid="sweepstakes-loading-skeleton">
          <SkeletonContent orientation="horizontal" disablePadding grow initialSize="full">
            <SkeletonContent disablePadding initialSize="1/4">
              <SkeletonBlock width={30} height={20} />
            </SkeletonContent>
            <SkeletonContent disablePadding initialSize="3/4">
              <SkeletonLine />
            </SkeletonContent>
          </SkeletonContent>
          <SkeletonContent orientation="vertical" disablePadding disableGutters grow>
            <SkeletonLine variant="single" fullWidth />
          </SkeletonContent>
        </Skeleton>
      ) : (
        <div className="sui-space-y-4">
          <div className="sui-flex sui-items-start sui-justify-between">
            <div className="sui-flex sui-items-start sui-gap-3 sui-flex-1">
              {sweepstakesData?.logo && (
                <img
                  src={sweepstakesData.logo}
                  alt="Sweepstakes Logo"
                  className="sui-w-12 sui-h-12 sui-flex-shrink-0 sui-mt-8"
                  width="48"
                  height="48"
                  data-testid="sweepstakes-logo"
                />
              )}
              <div className="sui-flex-1">
                <div className="sui-flex sui-items-center sui-gap-2 sui-mb-2">
                  <Badge color="success" variant="outline" data-testid="sweepstakes-status-badge">
                    {sweepstakesData?.status || 'New'}
                  </Badge>
                </div>
                {sweepstakesData?.title && (
                  <div className="sui-mb-1">
                    <Typography variant="h6" height="snug" data-testid="sweepstakes-title">
                      {sweepstakesData.title}
                    </Typography>
                  </div>
                )}
                {sweepstakesData?.description && (
                  <div className="sui-mb-2">
                    <Typography variant="body-sm" data-testid="sweepstakes-description">
                      {sweepstakesData.description}
                    </Typography>
                  </div>
                )}
                {sweepstakesData?.expirationDate && (
                  <Typography variant="body-base" data-testid="sweepstakes-expiration">
                    {formatExpirationDate(sweepstakesData.expirationDate)}
                  </Typography>
                )}
              </div>
            </div>
            <button
              type="button"
              className={`sui-text-brand sui-p-2 sui-cursor-pointer sui-rounded-md 
              sui-transition-all hover:sui-bg-gray-50`}
              onClick={handleDetailsClick}
              aria-label="View sweepstakes details"
              data-testid="sweepstakes-details-button"
            >
              <ArrowForward />
            </button>
          </div>
          {sweepstakesData?.steps && sweepstakesData.steps.length > 0 && (
            <div data-testid="sweepstakes-progress-section">
              <ProgressStepper
                centerEdgeNodes
                nonLinear
                connector={<></>}
                data-testid="sweepstakes-progress-stepper"
              >
                {sweepstakesData.steps.map((step, index) => (
                  <Step key={step.id} completed={step.completed} data-testid={`sweepstakes-step-${step.id}`}>
                    <StepButton
                      onClick={() => handleStepClick(step)}
                      icon={() => (
                        <img
                          src={step.iconUrl}
                          alt={`Step ${index + 1}`}
                          width={32}
                          height={32}
                          className="sui-rounded-full"
                          data-testid={`sweepstakes-step-icon-${step.id}`}
                        />
                      )}
                    />
                    <StepLabel title={step.id} />
                  </Step>
                ))}
              </ProgressStepper>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

SweepstakesEntryCard.propTypes = {
  sweepstakesData: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    logo: PropTypes.string,
    status: PropTypes.string,
    expirationDate: PropTypes.string,
    isOptedIn: PropTypes.bool,
    completedSteps: PropTypes.number,
    steps: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      iconUrl: PropTypes.string,
      completed: PropTypes.bool,
    })),
  }),
  onDetailsClick: PropTypes.func,
  onStepClick: PropTypes.func,
  isLoading: PropTypes.bool,
  hasError: PropTypes.bool,
};

SweepstakesEntryCard.defaultProps = {
  sweepstakesData: null,
  onDetailsClick: null,
  onStepClick: null,
  isLoading: false,
  hasError: false,
};